import { ChangeEvent, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { ArrowBackIosNew, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  Link,
  Skeleton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography as T,
  Tooltip,
  IconButton,
} from "@mui/material";
import MarkdownModal from "datasets/components/MarkdownModal";
import { TableLineageModal } from "datasets/components/TableLineageModal";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import useDatasetTable from "datasets/hooks/useDatasetTable";
import DatasetTableRow from "datasets/pages/tables/components/DatasetTableRow";
import { StyledOutlinedInput } from "datasets/pages/tables/components/ImportDetails";
import MarkdownCollapse from "datasets/pages/tables/components/MarkdownCollapse";
import TagsAutocomplete from "datasets/pages/tables/components/TagsAutocomplete";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import { BooleanParam, StringParam, useQueryParam, withDefault } from "use-query-params";
import yn from "yn";
import GenerateCodeButton from "~/components/GenerateCodeButton";
import PageHeader from "~/components/PageHeader";
import useErrorToast from "~/contexts/error-toast";
import { Column } from "~/types/interfaces/catalogResponse";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_DATASET_PERMISSIONS_ENABLED } = import.meta.env;

const StyledTHeaderCell = styled(TableCell)({
  fontWeight: 400,
  lineHeight: "20px",
  opacity: 0.6,
  padding: "8px 8px",
  "&:first-of-type": {
    paddingLeft: 52,
  },
  "&:last-of-type": {
    paddingRight: 16,
  },
});

const StyledTBodyCell = styled(TableCell)({
  padding: "20px 8px",
  position: "relative",
  "&:first-of-type": {
    paddingLeft: 52,
  },
  "&:last-of-type": {
    paddingRight: 16,
  },
});

const TableRowSkeleton = () => {
  return (
    <TableRow>
      {Array.from({ length: 4 }).map((_, i) => (
        <StyledTBodyCell key={i}>
          <Skeleton variant="rounded" height={20} width="100%" />
        </StyledTBodyCell>
      ))}
    </TableRow>
  );
};

const LineageButton = ({ onOpenLineage }: { onOpenLineage: () => void }) => {
  const { data: dataset } = useDataset();
  const { data: datasetTable } = useDatasetTable();

  if (yn(VITE_DATASET_PERMISSIONS_ENABLED) && dataset?.access_level !== DatasetAccessLevel.OWNER) {
    return null;
  }

  const handleLineage = () => {
    trackEvent("dataset_table_lineage_clicked", {
      dataset_id: dataset?.id,
      table_fqn: datasetTable?.fullyQualifiedName,
    });
    onOpenLineage();
  };

  return (
    <Button variant="outlined" onClick={handleLineage} data-testid="view-lineage-button">
      View Lineage
    </Button>
  );
};

const DatasetTable = () => {
  const { showError } = useErrorToast();
  const canEdit = useDatasetPermission("edit:catalog-metadata");
  const { data: datasetTable, isLoading, mutate } = useDatasetTable();
  const currentTags = datasetTable?.tags?.map((tag) => tag.tagFQN) ?? [];
  const [columnSearch, setColumnSearch] = useQueryParam("search", withDefault(StringParam, ""));
  const highlightText = columnSearch || "";
  const { columns = [] } = datasetTable ?? {};
  const location = [datasetTable?.databaseName, datasetTable?.databaseSchema].join(".");

  const [descriptionError, setDescriptionError] = useState("");
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [lineageModalOpen, setLineageModalOpen] = useQueryParam(
    "lineage",
    withDefault(BooleanParam, false)
  );
  const [selectedFields, setSelectedFields] = useState<Column[]>([]);
  const [showEditDescButton, setShowEditDescButton] = useState(false);
  const [updatingDescription, setUpdatingDescription] = useState(false);
  const [updatingTags, setUpdatingTags] = useState(false);

  const updateDescription = async (description: string) => {
    setDescriptionError("");
    setUpdatingDescription(true);

    try {
      const resp = await fetch(
        `${VITE_CATALOG_API}/tables/fqn/${datasetTable?.fullyQualifiedName}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ description }),
        }
      );

      if (resp.ok) {
        mutate();
        setDescriptionModalOpen(false);
      } else {
        throw new Error("Unable to update dataset description");
      }
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      setDescriptionError(e);
    }

    setUpdatingDescription(false);
  };

  const updateTags = async (tags: string[]) => {
    setUpdatingTags(true);

    try {
      const formattedTags = tags.map((tag) => ({ tagFQN: tag }));

      await mutate((data) => ({ ...data!, tags: formattedTags }), {
        revalidate: false,
        rollbackOnError: true,
      });

      const resp = await fetch(
        `${VITE_CATALOG_API}/tables/fqn/${datasetTable?.fullyQualifiedName}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tags: formattedTags }),
        }
      );

      mutate();

      if (!resp.ok) {
        throw new Error("Unable to update table");
      }
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      showError(e.message);
    }

    setUpdatingTags(false);
  };

  const filteredColumns = useMemo(
    () =>
      columns.filter((column) => {
        return (
          column.name.toLowerCase().includes(columnSearch.toLowerCase()) ||
          column.description?.toLowerCase().includes(columnSearch.toLowerCase())
        );
      }),
    [columnSearch, columns]
  );

  return (
    <Stack gap={1.5}>
      <Stack>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Link
            component={RouterLink}
            underline="hover"
            color="text.primary"
            to=".."
            sx={{
              opacity: 0.6,
              textDecoration: "none",
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <ArrowBackIosNew fontSize="inherit" />
            All Tables
          </Link>
          <LineageButton onOpenLineage={() => setLineageModalOpen(true)} />
        </Box>
        {isLoading ? (
          <Skeleton variant="rounded" height={24} width={400} />
        ) : (
          <PageHeader>{datasetTable?.name}</PageHeader>
        )}
      </Stack>
      {isLoading ? (
        <Skeleton variant="rounded" height={20} width={200} />
      ) : (
        <T variant="caption" color="grey.600" fontWeight={500}>
          Location: {location}
        </T>
      )}
      {isLoading ? (
        <Skeleton variant="rounded" height={100} />
      ) : (
        datasetTable && (
          <Box
            onMouseEnter={() => {
              if (canEdit) {
                setShowEditDescButton(true);
              }
            }}
            onMouseLeave={() => {
              if (canEdit) {
                setShowEditDescButton(false);
              }
            }}
            sx={{ position: "relative" }}
          >
            <MarkdownCollapse
              editable={canEdit}
              maxLines={5}
              value={datasetTable.description ?? ""}
            />
            {canEdit && (
              <>
                <Tooltip arrow title="Edit description" enterDelay={500}>
                  <IconButton
                    size="small"
                    sx={{
                      opacity: showEditDescButton ? 1 : 0,
                      position: "absolute",
                      right: 0,
                      top: -4,
                      transition: "opacity 150ms ease-in-out",
                    }}
                    onClick={() => {
                      setDescriptionModalOpen(true);
                    }}
                  >
                    <Edit sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                {descriptionModalOpen && (
                  <MarkdownModal
                    error={descriptionError}
                    loading={updatingDescription}
                    onClose={() => setDescriptionModalOpen(false)}
                    onSubmit={updateDescription}
                    open={descriptionModalOpen}
                    title="Edit Table Description"
                    value={datasetTable.description ?? ""}
                  />
                )}
              </>
            )}
          </Box>
        )
      )}
      <Stack gap={1}>
        <T variant="body2" fontWeight={500}>
          Table Tags
        </T>
        <Box maxWidth={600}>
          {isLoading ? (
            <Skeleton variant="rounded" height={36} width="100%" />
          ) : (
            <TagsAutocomplete
              disabled={updatingTags}
              readOnly={!canEdit}
              onSave={updateTags}
              value={currentTags}
            />
          )}
        </Box>
      </Stack>
      <Stack>
        <T variant="body2" fontWeight={500} mb={0.5}>
          Columns
        </T>
        <Stack gap={1.25}>
          <Box display="flex" alignItems="center" gap={1}>
            <StyledOutlinedInput
              name="Column search"
              fullWidth
              disabled={isLoading}
              size="small"
              placeholder="Search by column name..."
              sx={{ maxWidth: 320 }}
              value={columnSearch}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value;
                setColumnSearch(value, "replaceIn");
              }}
            />
            {datasetTable && !isLoading && (
              <GenerateCodeButton
                selections={[
                  {
                    schema: datasetTable.databaseSchema,
                    table: datasetTable.name,
                    variables: selectedFields.map(({ name }) => name),
                  },
                ]}
              />
            )}
          </Box>
          <TableContainer
            sx={{
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: "grey.300",
              borderRadius: 1.5,
            }}
          >
            <Table size="small">
              <TableHead
                sx={{
                  backgroundColor: "#E0E0E766",
                }}
              >
                <TableRow>
                  <StyledTHeaderCell sx={{ minWidth: 180 }}>Name</StyledTHeaderCell>
                  <StyledTHeaderCell sx={{ minWidth: 140 }}>Type</StyledTHeaderCell>
                  <StyledTHeaderCell sx={{ minWidth: 300 }}>Description</StyledTHeaderCell>
                  <StyledTHeaderCell sx={{ minWidth: 240 }}>Tags</StyledTHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <TableRowSkeleton key={i} />
                    ))}
                  </>
                ) : (
                  filteredColumns.map((column, i) => (
                    <DatasetTableRow
                      autoExpand={columnSearch === column.name ? true : null}
                      column={column}
                      highlightText={highlightText}
                      key={i}
                      selectedFields={selectedFields}
                      setSelectedFields={setSelectedFields}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Stack>
      <TableLineageModal
        open={lineageModalOpen}
        onOpenChange={setLineageModalOpen}
        tableFQN={datasetTable?.fullyQualifiedName}
        tableName={datasetTable?.name}
      />
    </Stack>
  );
};

export default DatasetTable;
